/**
 * The share panel: link (with options), QR code, and PDF export in three
 * labelled sections. Link/QR need a published story; PDF export does not.
 * @module Share
 * @category Display Components
 */

import "react-toggle/style.css";

import {
	StyledDialogPanel,
	StyledPillButton,
	StyledTitle,
	colors,
	focusVisible,
	measures,
	text,
} from "@quickpoint.me/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import QRCodeStyling, { type Extension } from "styled-qr-code";
import { ShowShare, StackCfKeyAtom } from "../state/stackAtoms";

import Toggle from "react-toggle";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import styled from "styled-components";
import { usePdfExport } from "../hooks/usePdfExport";
import { usePublishStack } from "../hooks/usePublishStack";
import { ViewerPageIndex } from "../state/atoms";
import { PublishAtom } from "../state/publish";
import { IsInPortrait } from "../state/selectors";
import { DemoMode } from "../state/uiAtoms";
import ToggleSharePanelButton from "./controls/ToggleSharePanel";

// fixed QR size: a self-measuring layout (the old approach) is fragile, and
// a QR this size scans fine
const QR_SIZE = 140;

const StyledPanel = styled.div`
  display: grid;
  height: 100%;
  /* header floor: 48px Done pill + air */
  grid-template-rows: max(11%, 68px) 1fr;
  grid-template-columns: 1fr;
  grid-template-areas:
    "title"
    "share";
`;

/* unpublished stories still get the lavender container: the notice plus the
   publish-independent PDF export */
const StyledUnpublishedBox = styled.div`
  grid-area: share;
  align-self: start;
  background-color: ${(props) => props.theme.stacksRollOverBackground};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 1.5rem;
  margin: ${measures.dialogPanel.landscape.margin};
  padding: 2rem;
  font-family: ${(props) => props.theme.font};
  color: ${(props) => props.theme.navigationText};

  p {
    margin: 0;
  }

  .pdf-row {
    display: flex;
    align-items: center;
    column-gap: 1rem;
  }
`;

const StyledShare = styled.div<{ $isInPortrait: boolean }>`
  background-color: ${(props) => props.theme.stacksRollOverBackground};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  font-family: ${(props) => props.theme.font};
  font-size: 16px;
  font-weight: 400;
  color: ${(props) => props.theme.navigationText};
  grid-area: share;
  /* never escape the dialog: shrink to the grid cell and scroll internally */
  min-height: 0;
  overflow-y: auto;
  margin: ${(props) =>
		props.$isInPortrait
			? measures.dialogPanel.portrait.margin
			: measures.dialogPanel.landscape.margin};
  padding: 0.5rem 2rem;

  section {
    display: flex;
    flex-direction: column;
    row-gap: 0.75rem;
    padding-block: 1rem;
  }

  section + section {
    border-top: 1px solid ${(props) => props.theme.navigationFrame};
  }

  h3 {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #4A4A4A; /* 7.6:1 on the lavender container */
  }

  .start-link {
    align-items: center;
    background-color: ${(props) => props.theme.backgroundColor};
    border-radius: 16px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    column-gap: 1rem;
    padding: 0.25rem 0.25rem 0.25rem 16px;
  }

  .start-link span {
    /* 8.2:1 on the white link pill */
    color: #4A4A4A;
    overflow-wrap: anywhere;
  }

  .link-options {
    display: flex;
    flex-wrap: wrap;
    column-gap: 2.5rem;
    row-gap: 0.75rem;
  }

  .page-toggle,
  .read-toggle {
    display: flex;
    flex-direction: row;
    align-items: center;

    .react-toggle {
      margin-inline-end: ${(props) => (props.$isInPortrait ? "8px" : ".6svw")};
    }

    .react-toggle--checked .react-toggle-track {
      background-color: ${colors.brand.purple};
    }
  }

  .qr-row {
    display: flex;
    align-items: center;
    column-gap: 1.5rem;
    flex-wrap: wrap;
    row-gap: 1rem;
    /* leave room for the absolutely centered QR on the right */
    padding-right: ${QR_SIZE + 24}px;
  }

  /* the QR centers against the whole section (heading included), not just
     the controls row — otherwise it hangs low */
  section:last-child {
    position: relative;
    min-height: ${QR_SIZE + 20}px;
    justify-content: center;
  }

  .download-control {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 0.5rem;
  }

  .download-stack {
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
    align-items: flex-start;
  }

  .qr-type-select {
    background-color: white;
    border-radius: 8px;
    /* visible boundary >=3:1 + comfortable target */
    border: 1px solid #767676;
    padding: 5px;
    min-height: 48px;
    cursor: pointer;
    font-family: ${(props) => props.theme.font};
    ${focusVisible}
  }

  .start-qr {
    width: ${QR_SIZE}px;
    height: ${QR_SIZE}px;
    flex-shrink: 0;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);

    svg {
      background-color: white;
      border-radius: 8px;
    }
  }

  .pdf-row {
    display: flex;
    align-items: center;
    column-gap: 1rem;
  }

  .pdf-hint {
    color: #4A4A4A;
    font-size: 0.9rem;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
`;

const StyledQrCode = styled.div``;

const Share = (): JSX.Element => {
	const isInPortrait = useRecoilValue(IsInPortrait);
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	// PublishAtom is an async selector (it suspends, see TopBar's Suspense
	// around PublishControl) — read it as a loadable since Share is always
	// mounted; while loading, treat the story as unpublished
	const publishLoadable = useRecoilValueLoadable(PublishAtom);
	const isPublished =
		publishLoadable.state === "hasValue" && !!publishLoadable.contents;
	const inDemoMode = useRecoilValue(DemoMode);
	const { pdfStatus, canExportPdf, handleExportPdf } = usePdfExport();
	const { publishing, canPublish, publishStack } = usePublishStack();
	// links/QR point at the public URL, so they need a published story;
	// PDF export renders from the saved stack and does not
	const canUseShareLinks = isPublished || inDemoMode;

	const showShare = useRecoilValue(ShowShare);
	const currentPage = useRecoilValue(ViewerPageIndex);
	const qrRef = useRef<HTMLDivElement>(null);
	const [fileExt, setFileExt] = useState<Extension>("png");
	const [startAtPage, setStartAtPage] = useState(false);
	const [readMode, setReadMode] = useState(false);

	const [url, setUrl] = useState(`https://quickpoint.me/${stackCfKey}`);
	const [copied, setCopied] = useState(false);
	// announced via role=status when a toggle rewrites the link
	const [linkAnnouncement, setLinkAnnouncement] = useState("");
	const urlInitialized = useRef(false);

	const qrCode = useMemo(
		() =>
			new QRCodeStyling({
				width: QR_SIZE,
				height: QR_SIZE,
				type: "svg",
				margin: 14,
				image: "/QPLogo.svg",
				dotsOptions: {
					color: colors.teal,
					type: "extra-rounded",
				},
				cornersSquareOptions: { type: "extra-rounded" },
				imageOptions: {
					crossOrigin: "anonymous",
					margin: 2,
				},
				backgroundOptions: {
					color: "#e9ebee",
				},
			}),
		[],
	);

	const handleToggle = () => {
		setStartAtPage(!startAtPage);
	};

	const handleReadToggle = () => {
		setReadMode(!readMode);
	};

	const onExtensionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		// @ts-expect-error: event.target.value is a string
		setFileExt(event.target.value);
	};

	const onDownloadClick = () => {
		qrCode.download({
			name: `${stackCfKey}${startAtPage ? `-page-${currentPage}` : ""}`,
			extension: fileExt,
		});
	};

	const copyToClipboard = () => {
		navigator.clipboard
			.writeText(url)
			.then(() => {
				setCopied(true);
				setTimeout(() => {
					setCopied(false);
				}, 1500);
			})
			.catch((err) => {
				console.error(err);
			});
	};

	useEffect(() => {
		qrCode.update({
			data: url,
		});
	}, [url, qrCode]);

	// attach the QR svg once its container exists — the container only
	// renders when the story is shareable, so that state is a dependency
	useEffect(() => {
		let qr: null | HTMLElement = null;
		if (qrRef.current) {
			qr = qrRef.current;
			qrCode.append(qrRef.current);
		}
		return () => {
			if (!qr) return;
			qr.innerHTML = "";
		};
	}, [qrCode, canUseShareLinks]);

	useEffect(() => {
		const newUrl = startAtPage
			? `https://quickpoint.me/${stackCfKey}${readMode ? "/READ" : ""}/PAGE/${currentPage}`
			: readMode
				? `https://quickpoint.me/${stackCfKey}/READ`
				: `https://quickpoint.me/${stackCfKey}`;
		setUrl(newUrl);
		// tell screen reader users the link changed (skip the initial set;
		// clear afterwards so the same message re-announces next time)
		if (urlInitialized.current) {
			setLinkAnnouncement(text.share.linkUpdated);
			const timer = setTimeout(() => setLinkAnnouncement(""), 1200);
			return () => clearTimeout(timer);
		}
		urlInitialized.current = true;
		return undefined;
	}, [startAtPage, stackCfKey, currentPage, readMode]);

	const pdfButton = (variant: "dark" | "primaryOutline" = "dark") => (
		<StyledPillButton
			$variant={variant}
			data-testid="share-pdf-export"
			onClick={handleExportPdf}
			disabled={!canExportPdf || pdfStatus === "loading"}
			aria-busy={pdfStatus === "loading"}
		>
			{pdfStatus === "loading"
				? text.preview.exportPdfLoading
				: text.preview.exportPdf}
		</StyledPillButton>
	);

	return (
		<StyledDialogPanel
			$show={showShare}
			$isInPortrait={isInPortrait}
			data-testid="share"
			id="share-panel"
		>
			<StyledPanel className="styled-panel">
				<StyledTitle $isInPortrait={isInPortrait} className="share-title">
					{text.share.share}
					<ToggleSharePanelButton />
				</StyledTitle>
				{!canUseShareLinks && (
					<StyledUnpublishedBox data-testid="share-unpublished-notice">
						<p>{text.ui.publishBeforeShare}</p>
						<div className="pdf-row">
							<StyledPillButton
								data-testid="share-publish-button"
								onClick={publishStack}
								disabled={!canPublish || publishing}
								aria-busy={publishing}
							>
								{text.controls.publish}
							</StyledPillButton>
							{pdfButton("primaryOutline")}
						</div>
					</StyledUnpublishedBox>
				)}
				{canUseShareLinks && (
					<StyledShare data-testid="styled-share" $isInPortrait={isInPortrait}>
						<section aria-labelledby="share-link-heading">
							<h3 id="share-link-heading">{text.share.linkSection}</h3>
							<div className="start-link">
								<span>{url}</span>
								<StyledPillButton
									className="copy-link-button"
									data-testid="copy-link-button"
									onClick={() => copyToClipboard()}
								>
									{copied ? text.share.linkCopied : text.share.copyLink}
								</StyledPillButton>
							</div>
							<div className="link-options">
								<div className="page-toggle">
									{/* @ts-ignore */}
									<Toggle
										id="page-status"
										defaultChecked={startAtPage}
										aria-labelledby="page-label"
										onChange={() => handleToggle()}
									/>
									<div id="page-label" className="page-toggle-label">
										{startAtPage
											? (text.share.linkStartsAtPage as (page: number) => string)(
													currentPage,
												)
											: text.share.startAtPage}
									</div>
								</div>
								<div className="read-toggle">
									{/* @ts-ignore */}
									<Toggle
										id="read-status"
										defaultChecked={readMode}
										aria-labelledby="read-label"
										onChange={() => handleReadToggle()}
									/>
									<div id="read-label" className="page-toggle-label">
										{readMode
											? text.share.readModeOn
											: text.share.shareInReadMode}
									</div>
								</div>
							</div>
							<span className="visually-hidden" role="status">
								{copied ? text.share.linkCopied : linkAnnouncement}
							</span>
						</section>

						<section aria-labelledby="share-downloads-heading">
							<h3 id="share-downloads-heading">{text.share.downloadsSection}</h3>
							<div className="qr-row">
								<div className="download-stack">
									<div className="download-control">
										<select
											className="qr-type-select"
											onChange={onExtensionChange}
											value={fileExt}
											aria-label={text.share.downloadQrAs}
										>
											<option value="svg">SVG</option>
											<option value="png">PNG</option>
											<option value="jpeg">JPEG</option>
											<option value="webp">WEBP</option>
										</select>
										<StyledPillButton
											$variant="dark"
											data-testid="download-qr-button"
											onClick={onDownloadClick}
										>
											{text.share.downloadQr}
										</StyledPillButton>
									</div>
									<div className="pdf-row">
										{pdfButton()}
										<span className="pdf-hint">{text.share.pdfHint}</span>
									</div>
								</div>
								<StyledQrCode
									className="start-qr"
									ref={qrRef}
									role="img"
									aria-label={text.share.qrCodeLabel}
								/>
							</div>
						</section>
					</StyledShare>
				)}
			</StyledPanel>
		</StyledDialogPanel>
	);
};

export default Share;
